import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';

import { ONBOARDING_API_CONFIG, OnboardingApiConfigInterface } from '@planone/bau365/data-access-onboarding';
import {
  EventMockGenerator,
  MOCK_API,
  MockEnvironmentInterface,
} from '@planone/bau365/util-api-response-mock-generators';
import { Event } from '@planone/bau365/util-fair-data';
import { CursorPaginatedResponse } from '@planone/shared/util-api-cursor-handling';
import { concatMap, map, Observable, of } from 'rxjs';

import { EventListRequest } from '../../interfaces/event.interfaces';

@Injectable({
  providedIn: 'root',
})
export class DataAccessEventsService {
  constructor(
    private httpClient: HttpClient,
    @Inject(LOCALE_ID) private locale: string,
    @Inject(ONBOARDING_API_CONFIG)
    private onboarding_api_config: OnboardingApiConfigInterface,
    @Inject(MOCK_API) private mockConfig: MockEnvironmentInterface
  ) {}

  private static emptyArrayToUndefined<T>(inputArray: T[] | null | undefined): T[] | undefined {
    if (!inputArray) return undefined;

    const filteredArray = inputArray.filter((value: T) => !!value);

    if (filteredArray.length <= 0) return undefined;

    return filteredArray;
  }

  private static transformEvent(event: Event): Event {
    return {
      ...event,
      contributors: this.emptyArrayToUndefined(event.contributors),
    };
  }

  getEvents(
    fairId: string,
    cursor: string | undefined = undefined,
    limit: number | undefined = undefined,
    exhibitorId: string | undefined = undefined
  ): Observable<CursorPaginatedResponse<Event>> {
    const requestParams: EventListRequest = {};

    if (typeof cursor !== 'undefined') {
      requestParams['cursor'] = cursor;
    }

    if (typeof limit !== 'undefined') {
      requestParams['limit'] = limit;
    }

    if (typeof exhibitorId !== 'undefined') {
      requestParams['exhibitor_id'] = exhibitorId;
    }

    if (this.mockConfig.globally) {
      return of(EventMockGenerator.getPaginatedList(exhibitorId));
    }

    return this.httpClient
      .get<CursorPaginatedResponse<Event>>(`${this.onboarding_api_config.fairDataGatewayUrl}/fairs/${fairId}/events`, {
        params: { ...requestParams },
      })
      .pipe(
        map((response: CursorPaginatedResponse<Event>) => {
          return {
            ...response,
            items: response.items.map((event: Event) => DataAccessEventsService.transformEvent(event)),
          };
        })
      );
  }

  getEvent(fairId: string, eventId: string) {
    if (this.mockConfig.globally) {
      return of(EventMockGenerator.getObject(eventId, undefined));
    }

    return this.httpClient
      .get<Event>(`${this.onboarding_api_config.fairDataGatewayUrl}/fairs/${fairId}/events/${eventId}`)
      .pipe(map((event: Event) => DataAccessEventsService.transformEvent(event)));
  }

  getEventsByIds(fairId: string, ids: string[] | undefined) {
    if (this.mockConfig.globally) {
      return of(EventMockGenerator.getList(ids));
    }

    if (!ids) {
      return <Observable<Event[]>>of([]);
    }

    let observable: Observable<Event[]> = of([]);

    ids.forEach((id: string) => {
      observable = observable.pipe(
        concatMap((list) => this.getEvent(fairId, id).pipe(map((event: Event) => list.concat([event]))))
      );
    });

    return observable;
  }
}
